import {
	Color,
	Container,
	GlProgram,
	Graphics,
	MeshSimple,
	Shader,
	Texture,
} from 'pixi.js';

const DEFAULT_PORTAL_COLOR = '#0093ff';
const PROJECTILE_SIZE = 68;
const MAX_TRAIL_PARTICLES = 56;

export const PORTAL_OPEN_DURATION = 0.62;
export const PORTAL_OPEN_EFFECT_DURATION = 1.15;

const clamp01 = value => Math.max(0, Math.min(1, Number(value) || 0));
const easeOutCubic = value => 1 - Math.pow(1 - clamp01(value), 3);
const easeOutBack = value => {
	const progress = clamp01(value);
	const overshoot = 1.28;
	return 1 + (overshoot + 1) * Math.pow(progress - 1, 3) +
		overshoot * Math.pow(progress - 1, 2);
};

const colorArray = color => {
	try {
		return new Color(color || DEFAULT_PORTAL_COLOR).toArray().slice(0, 3);
	} catch(_error) {
		return new Color(DEFAULT_PORTAL_COLOR).toArray().slice(0, 3);
	}
};

const colorNumber = values => values.reduce(
	(result, value) => (result << 8) | Math.round(clamp01(value) * 255),
	0,
);

const mixColor = (values, target, amount) => colorNumber(values.map(
	(value, index) => value + (target[index] - value) * amount,
));

const effectPalette = color => {
	const selected = colorArray(color);
	return {
		base: colorNumber(selected),
		light: mixColor(selected, [1, 1, 1], 0.48),
		hot: mixColor(selected, [1, 1, 1], 0.84),
	};
};

export const getPortalOpeningTransform = progress => {
	const time = clamp01(progress);
	const stretch = easeOutBack(clamp01((time - 0.025) / 0.78));
	const widen = easeOutCubic(clamp01((time - 0.08) / 0.78));
	return {
		alpha: easeOutCubic(clamp01(time / 0.18)),
		scaleX: 0.18 + widen * 0.82,
		scaleY: 0.025 + stretch * 0.975,
	};
};

const PROJECTILE_VERTEX = `
in vec2 aPosition;
in vec2 aUV;

out vec2 vUV;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

void main(void)
{
	mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
	gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
	vUV = aUV;
}`;

const PROJECTILE_FRAGMENT = `
in vec2 vUV;

uniform vec4 uPlasmaColor;
uniform float uTime;
uniform float uSeed;

float hash21(vec2 point)
{
	point = fract(point * vec2(127.1, 311.7));
	point += dot(point, point + 31.19);
	return fract(point.x * point.y);
}

float noise21(vec2 point)
{
	vec2 cell = floor(point);
	vec2 local = fract(point);
	local = local * local * (3.0 - 2.0 * local);
	return mix(
		mix(hash21(cell), hash21(cell + vec2(1.0, 0.0)), local.x),
		mix(hash21(cell + vec2(0.0, 1.0)), hash21(cell + vec2(1.0)), local.x),
		local.y
	);
}

void main(void)
{
	vec2 point = (vUV - 0.5) * 2.0;
	float radius = length(point);
	float angle = atan(point.y, point.x);
	float time = uTime * 5.2 + uSeed * 17.0;

	float boiling = noise21(point * 4.1 + vec2(time * 0.17, -time * 0.13));
	float fine = noise21(point * 9.3 + vec2(-time * 0.24, time * 0.19));
	float boundary = 0.52
		+ sin(angle * 7.0 + time + boiling * 2.2) * 0.035
		+ (fine - 0.5) * 0.055;
	float sphere = 1.0 - smoothstep(boundary - 0.07, boundary + 0.025, radius);
	float glow = (1.0 - smoothstep(0.42, 1.0, radius)) * 0.28;

	float spiralA = 0.5 + 0.5 * sin(angle * 4.0 - radius * 18.0 + time * 1.25 + boiling * 3.0);
	float spiralB = 0.5 + 0.5 * sin(angle * -3.0 - radius * 13.0 - time * 0.92 + fine * 2.4);
	float filaments = smoothstep(0.68, 0.96, max(spiralA, spiralB));
	filaments *= sphere * smoothstep(0.08, 0.52, radius);

	vec2 coreOffset = vec2(sin(time * 0.73), cos(time * 0.91)) * 0.075;
	float core = 1.0 - smoothstep(0.03, 0.25, length(point - coreOffset));
	float corona = smoothstep(0.33, 0.52, radius) *
		(1.0 - smoothstep(0.50, 0.70, radius));
	corona *= 0.55 + 0.45 * sin(angle * 9.0 - time * 1.4 + fine * 4.0);

	vec3 selected = uPlasmaColor.rgb;
	vec3 deep = clamp(selected * 0.42 + vec3(0.025), 0.0, 1.0);
	vec3 light = mix(selected, vec3(1.0), 0.52);
	vec3 hot = mix(selected, vec3(1.0), 0.90);
	vec3 plasma = mix(deep, selected, 0.48 + boiling * 0.34);
	plasma = mix(plasma, light, filaments * 0.78);
	plasma = mix(plasma, hot, core * 0.92 + corona * 0.42);

	float pulse = 0.94 + sin(time * 1.7) * 0.06;
	float alpha = clamp(sphere + glow + corona * 0.22, 0.0, 1.0);
	if(alpha <= 0.002) discard;
	gl_FragColor = vec4(plasma * pulse * alpha, alpha);
}`;

class PortalProjectileShader extends Shader {
	constructor(color, seed) {
		super({
			glProgram: GlProgram.from({
				vertex: PROJECTILE_VERTEX,
				fragment: PROJECTILE_FRAGMENT,
				name: 'portal-plasma-projectile-shader',
			}),
			resources: {
				plasmaUniforms: {
					uPlasmaColor: {
						value: new Float32Array([1, 1, 1, 1]),
						type: 'vec4<f32>',
					},
					uTime: {value: 0, type: 'f32'},
					uSeed: {value: seed, type: 'f32'},
				},
			},
		});
		this.setColor(color);
	}

	setColor(color) {
		const [red, green, blue] = colorArray(color);
		this.resources.plasmaUniforms.uniforms.uPlasmaColor = new Float32Array([
			red,
			green,
			blue,
			1,
		]);
	}

	setTime(time) {
		this.resources.plasmaUniforms.uniforms.uTime = time;
	}
}

const createProjectileMesh = (color, seed) => {
	const halfSize = PROJECTILE_SIZE / 2;
	const shader = new PortalProjectileShader(color, seed);
	const mesh = new MeshSimple({
		texture: Texture.WHITE,
		shader,
		vertices: new Float32Array([
			-halfSize, -halfSize,
			halfSize, -halfSize,
			halfSize, halfSize,
			-halfSize, halfSize,
		]),
		uvs: new Float32Array([
			0, 0,
			1, 0,
			1, 1,
			0, 1,
		]),
		indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
	});
	mesh.label = 'portal-plasma-projectile';
	mesh.eventMode = 'none';
	mesh.autoUpdate = false;
	mesh.blendMode = 'add';
	mesh.onRender = () => shader.setTime(performance.now() * 0.001);
	return {mesh, shader};
};

export class PortalProjectileVisual {
	constructor(body, color) {
		this.body = body;
		this.color = color || DEFAULT_PORTAL_COLOR;
		this.palette = effectPalette(this.color);
		this.elapsed = 0;
		this.emissionAccumulator = 1;
		this.trailParticles = [];
		this.trailPool = [];
		this.lastPosition = null;
		this.seed = Math.random();

		const {mesh, shader} = createProjectileMesh(this.color, this.seed);
		this.mesh = mesh;
		this.shader = shader;
		this.mesh.scale.set(0.55);
		this.corona = new Graphics();
		this.corona.label = 'portal-projectile-corona';
		this.corona.eventMode = 'none';
		this.corona.blendMode = 'add';
		this.drawCorona();
		this.corona.scale.set(0.5);

		if(this.body?.originalGraphic) this.body.originalGraphic.visible = false;
		if(this.body?.mySprite) {
			this.body.mySprite.addChild(this.corona);
			this.body.mySprite.addChild(this.mesh);
		}

		this.trailContainer = new Container();
		this.trailContainer.label = 'portal-projectile-trail';
		this.trailContainer.eventMode = 'none';
		const parent = this.body?.mySprite?.parent;
		if(parent) {
			const projectileIndex = parent.getChildIndex(this.body.mySprite);
			parent.addChildAt(this.trailContainer, Math.max(0, projectileIndex));
			this.lastPosition = {
				x: this.body.mySprite.x,
				y: this.body.mySprite.y,
			};
		}
	}

	drawCorona() {
		if(!this.corona) return;
		this.corona.clear();
		this.corona.circle(0, 0, 18).stroke({
			width: 1.6,
			color: this.palette.light,
			alpha: 0.45,
		});
		for(let index = 0; index < 9; index++) {
			const angle = index / 9 * Math.PI * 2;
			const inner = 17 + (index % 2) * 2;
			const outer = 23 + (index % 3) * 2;
			this.corona.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
			this.corona.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
		}
		this.corona.stroke({width: 1.4, color: this.palette.hot, alpha: 0.52});
	}

	setColor(color) {
		this.color = color || DEFAULT_PORTAL_COLOR;
		this.palette = effectPalette(this.color);
		this.shader?.setColor(this.color);
		this.drawCorona();
	}

	spawnTrailParticle(x, y, directionX, directionY) {
		if(!this.trailContainer || this.trailParticles.length >= MAX_TRAIL_PARTICLES) return;
		const particle = this.trailPool.pop() || new Graphics();
		const size = 2.8 + Math.random() * 4.4;
		particle.clear();
		particle.circle(0, 0, size * 1.8).fill({color: this.palette.base, alpha: 0.10});
		particle.circle(0, 0, size).fill({color: this.palette.light, alpha: 0.54});
		particle.circle(-size * 0.24, -size * 0.22, size * 0.38)
			.fill({color: this.palette.hot, alpha: 0.88});
		particle.blendMode = 'add';
		particle.alpha = 1;
		particle.visible = true;
		particle.position.set(x, y);
		const sideways = (Math.random() - 0.5) * 25;
		const backwards = 12 + Math.random() * 34;
		particle.__portalTrail = {
			age: 0,
			life: 0.18 + Math.random() * 0.20,
			vx: -directionX * backwards - directionY * sideways,
			vy: -directionY * backwards + directionX * sideways,
			startScale: 0.58 + Math.random() * 0.58,
		};
		particle.scale.set(particle.__portalTrail.startScale);
		this.trailContainer.addChild(particle);
		this.trailParticles.push(particle);
	}

	emitTrail(deltaSeconds) {
		if(!this.body?.mySprite || !this.trailContainer) return;
		const current = {x: this.body.mySprite.x, y: this.body.mySprite.y};
		if(!this.lastPosition) this.lastPosition = current;
		const offsetX = current.x - this.lastPosition.x;
		const offsetY = current.y - this.lastPosition.y;
		const distance = Math.hypot(offsetX, offsetY);
		const angle = distance > 0.001 ? Math.atan2(offsetY, offsetX) : this.body.mySprite.rotation;
		const directionX = Math.cos(angle);
		const directionY = Math.sin(angle);
		const distanceSpawns = Math.min(7, Math.floor(distance / 7));

		for(let index = 0; index < distanceSpawns; index++) {
			const amount = (index + 1) / (distanceSpawns + 1);
			const jitter = (Math.random() - 0.5) * 5;
			this.spawnTrailParticle(
				this.lastPosition.x + offsetX * amount - directionY * jitter,
				this.lastPosition.y + offsetY * amount + directionX * jitter,
				directionX,
				directionY,
			);
		}

		this.emissionAccumulator += deltaSeconds * 36;
		const idleSpawns = Math.min(3, Math.floor(this.emissionAccumulator));
		this.emissionAccumulator -= idleSpawns;
		for(let index = 0; index < idleSpawns; index++) {
			const jitter = (Math.random() - 0.5) * 7;
			this.spawnTrailParticle(
				current.x - directionX * (7 + Math.random() * 8) - directionY * jitter,
				current.y - directionY * (7 + Math.random() * 8) + directionX * jitter,
				directionX,
				directionY,
			);
		}
		this.lastPosition = current;
	}

	updateTrail(deltaSeconds) {
		const drag = Math.pow(0.93, deltaSeconds * 60);
		for(let index = this.trailParticles.length - 1; index >= 0; index--) {
			const particle = this.trailParticles[index];
			const state = particle.__portalTrail;
			state.age += deltaSeconds;
			if(state.age >= state.life) {
				this.trailParticles.splice(index, 1);
				particle.removeFromParent();
				particle.visible = false;
				this.trailPool.push(particle);
				continue;
			}
			const remaining = 1 - state.age / state.life;
			state.vx *= drag;
			state.vy *= drag;
			particle.x += state.vx * deltaSeconds;
			particle.y += state.vy * deltaSeconds;
			particle.alpha = Math.min(1, remaining * 2.4) * remaining;
			const scale = state.startScale * (0.35 + remaining * 0.65);
			particle.scale.set(scale);
		}
	}

	update(deltaSeconds = 0) {
		const delta = Math.max(0, Math.min(0.06, Number(deltaSeconds) || 0));
		this.elapsed += delta;
		const launch = easeOutCubic(this.elapsed / 0.08);
		const pulse = 0.94 + Math.sin(this.elapsed * 17 + this.seed * 9) * 0.065;
		if(this.mesh) this.mesh.scale.set(pulse * (0.55 + launch * 0.45));
		if(this.corona) {
			this.corona.rotation -= delta * 4.8;
			this.corona.alpha = 0.72 + Math.sin(this.elapsed * 21) * 0.20;
			this.corona.scale.set(
				(0.92 + Math.sin(this.elapsed * 13) * 0.08) * (0.5 + launch * 0.5),
			);
		}
		this.emitTrail(delta);
		this.updateTrail(delta);
	}

	destroy() {
		if(this.mesh && !this.mesh.destroyed) {
			this.mesh.onRender = null;
			this.mesh.removeFromParent();
			this.mesh.destroy({texture: false, textureSource: false});
		}
		this.shader?.destroy();
		if(this.corona && !this.corona.destroyed) this.corona.destroy();
		for(const particle of [...this.trailParticles, ...this.trailPool]) {
			if(!particle.destroyed) particle.destroy();
		}
		if(this.trailContainer && !this.trailContainer.destroyed) {
			this.trailContainer.destroy({children: false});
		}
		this.trailParticles.length = 0;
		this.trailPool.length = 0;
		this.mesh = null;
		this.shader = null;
		this.corona = null;
		this.trailContainer = null;
		this.body = null;
	}
}

export class PortalMuzzleFlash {
	constructor({parent, anchor = null, position, angle, color}) {
		this.age = 0;
		this.duration = 0.32;
		this.palette = effectPalette(color);
		this.container = new Container();
		this.container.label = 'portal-gun-muzzle-flash';
		this.container.eventMode = 'none';
		this.container.position.set(position.x, position.y);
		this.container.rotation = angle;

		this.aura = new Graphics();
		this.aura.blendMode = 'add';
		this.aura.ellipse(11, 0, 35, 20).fill({color: this.palette.base, alpha: 0.20});
		this.aura.ellipse(10, 0, 23, 12).fill({color: this.palette.light, alpha: 0.26});
		this.container.addChild(this.aura);

		this.blast = new Graphics();
		this.blast.blendMode = 'add';
		this.blast.moveTo(-7, -6)
			.bezierCurveTo(8, -12, 35, -9, 54, 0)
			.bezierCurveTo(34, 9, 8, 12, -7, 6)
			.closePath()
			.fill({color: this.palette.light, alpha: 0.66});
		this.blast.ellipse(5, 0, 16, 7).fill({color: this.palette.hot, alpha: 0.86});
		this.container.addChild(this.blast);

		this.ring = new Graphics();
		this.ring.blendMode = 'add';
		this.ring.ellipse(0, 0, 14, 10).stroke({
			width: 3,
			color: this.palette.hot,
			alpha: 0.82,
		});
		this.container.addChild(this.ring);

		this.sparks = [];
		for(let index = 0; index < 10; index++) {
			const spark = new Graphics();
			const length = 5 + Math.random() * 12;
			spark.moveTo(-length * 0.5, 0).lineTo(length * 0.5, 0).stroke({
				width: 1.3 + Math.random() * 1.6,
				color: index % 3 ? this.palette.light : this.palette.hot,
				alpha: 0.90,
			});
			spark.blendMode = 'add';
			spark.position.set(2 + Math.random() * 13, (Math.random() - 0.5) * 10);
			const spread = (Math.random() - 0.5) * 1.05;
			const speed = 95 + Math.random() * 210;
			spark.__portalMuzzleSpark = {
				vx: Math.cos(spread) * speed,
				vy: Math.sin(spread) * speed,
				spin: (Math.random() - 0.5) * 9,
			};
			spark.rotation = Math.atan2(
				spark.__portalMuzzleSpark.vy,
				spark.__portalMuzzleSpark.vx,
			);
			this.container.addChild(spark);
			this.sparks.push(spark);
		}

		if(parent) {
			if(anchor?.parent === parent) {
				const index = parent.getChildIndex(anchor);
				parent.addChildAt(this.container, Math.min(index + 1, parent.children.length));
			} else {
				parent.addChild(this.container);
			}
		}
	}

	update(deltaSeconds = 0) {
		const delta = Math.max(0, Math.min(0.06, Number(deltaSeconds) || 0));
		this.age += delta;
		const progress = clamp01(this.age / this.duration);
		const remaining = 1 - progress;
		this.aura.alpha = remaining * remaining;
		this.aura.scale.set(0.72 + progress * 1.05, 0.82 + progress * 0.48);
		this.blast.alpha = remaining * remaining;
		this.blast.scale.set(0.82 + progress * 0.75, 1 - progress * 0.46);
		this.ring.alpha = remaining * 0.88;
		this.ring.scale.set(0.45 + progress * 2.25, 0.45 + progress * 1.45);
		for(const spark of this.sparks) {
			const state = spark.__portalMuzzleSpark;
			spark.x += state.vx * delta;
			spark.y += state.vy * delta;
			spark.rotation += state.spin * delta;
			spark.alpha = remaining * remaining;
		}
		return this.age < this.duration;
	}

	destroy() {
		if(this.container && !this.container.destroyed) {
			this.container.destroy({children: true});
		}
		this.sparks.length = 0;
		this.container = null;
	}
}

// A portal shot striking a surface that cannot hold a portal: a flattened
// splash pressed against the wall, a ripple spreading along it, sparks that
// fly back out along the surface normal and a scorch that lingers a moment.
// Local +x points away from the wall, as the impact normal does.
export class PortalImpactBurst {
	constructor({parent, position, angle, color}) {
		this.age = 0;
		this.duration = 0.46;
		this.palette = effectPalette(color);
		this.container = new Container();
		this.container.label = 'portal-impact-burst';
		this.container.eventMode = 'none';
		this.container.position.set(position.x, position.y);
		this.container.rotation = angle;

		this.scorch = new Graphics();
		this.scorch.blendMode = 'add';
		this.scorch.ellipse(1, 0, 6, 22).fill({color: this.palette.base, alpha: 0.30});
		this.container.addChild(this.scorch);

		this.splash = new Graphics();
		this.splash.blendMode = 'add';
		this.splash.ellipse(5, 0, 12, 30).fill({color: this.palette.base, alpha: 0.24});
		this.splash.ellipse(4, 0, 8, 18).fill({color: this.palette.light, alpha: 0.42});
		this.container.addChild(this.splash);

		this.flash = new Graphics();
		this.flash.blendMode = 'add';
		this.flash.ellipse(3, 0, 7, 11).fill({color: this.palette.hot, alpha: 0.9});
		this.container.addChild(this.flash);

		this.ring = new Graphics();
		this.ring.blendMode = 'add';
		this.ring.ellipse(0, 0, 5, 12).stroke({
			width: 2.4,
			color: this.palette.hot,
			alpha: 0.8,
		});
		this.container.addChild(this.ring);

		this.sparks = [];
		for(let index = 0; index < 12; index++) {
			const spark = new Graphics();
			const length = 4 + Math.random() * 9;
			spark.moveTo(-length * 0.5, 0).lineTo(length * 0.5, 0).stroke({
				width: 1.2 + Math.random() * 1.4,
				color: index % 3 ? this.palette.light : this.palette.hot,
				alpha: 0.9,
			});
			spark.blendMode = 'add';
			// Most sparks bounce back out; a few skid along the wall.
			const skids = index % 4 === 3;
			const spread = skids ?
				(Math.random() < 0.5 ? 1 : -1) * (1.25 + Math.random() * 0.25) :
				(Math.random() - 0.5) * 1.9;
			const speed = (skids ? 150 : 110) + Math.random() * 200;
			spark.position.set(1 + Math.random() * 3, (Math.random() - 0.5) * 8);
			spark.__portalImpactSpark = {
				vx: Math.cos(spread) * speed,
				vy: Math.sin(spread) * speed,
				spin: (Math.random() - 0.5) * 12,
			};
			spark.rotation = Math.atan2(
				spark.__portalImpactSpark.vy,
				spark.__portalImpactSpark.vx,
			);
			this.container.addChild(spark);
			this.sparks.push(spark);
		}

		parent?.addChild(this.container);
	}

	update(deltaSeconds = 0) {
		const delta = Math.max(0, Math.min(0.06, Number(deltaSeconds) || 0));
		this.age += delta;
		const progress = clamp01(this.age / this.duration);
		const remaining = 1 - progress;
		const eased = easeOutCubic(progress);
		this.flash.alpha = remaining * remaining * remaining;
		this.flash.scale.set(1 + eased * 0.6, 1 + eased * 1.4);
		this.splash.alpha = remaining * remaining;
		this.splash.scale.set(0.6 + eased * 0.5, 0.5 + eased * 1.3);
		this.ring.alpha = remaining * 0.85;
		this.ring.scale.set(0.5 + eased * 1.1, 0.4 + eased * 3.2);
		this.scorch.alpha = 0.6 + 0.4 * remaining;
		this.scorch.scale.set(1, 0.7 + eased * 0.9);
		const drag = Math.max(0, 1 - delta * 5.5);
		for(const spark of this.sparks) {
			const state = spark.__portalImpactSpark;
			spark.x += state.vx * delta;
			spark.y += state.vy * delta;
			state.vx *= drag;
			state.vy *= drag;
			spark.rotation += state.spin * delta;
			spark.alpha = remaining * remaining;
		}
		return this.age < this.duration;
	}

	destroy() {
		if(this.container && !this.container.destroyed) {
			this.container.destroy({children: true});
		}
		this.sparks.length = 0;
		this.container = null;
	}
}

export class PortalOpeningBurst {
	constructor({parent, color, width, height}) {
		this.age = 0;
		this.palette = effectPalette(color);
		this.halfWidth = width / 2;
		this.halfHeight = height / 2;
		this.emissionAccumulator = 8;
		this.particles = [];
		this.container = new Container();
		this.container.label = 'portal-opening-burst';
		this.container.eventMode = 'none';

		this.flare = new Graphics();
		this.flare.blendMode = 'add';
		this.ring = new Graphics();
		this.ring.blendMode = 'add';
		this.shockRing = new Graphics();
		this.shockRing.blendMode = 'add';
		this.container.addChild(this.flare);
		this.container.addChild(this.ring);
		this.container.addChild(this.shockRing);
		this.draw(color);
		parent?.addChild(this.container);
	}

	draw(color) {
		this.palette = effectPalette(color);
		this.flare.clear();
		this.flare.ellipse(0, 0, this.halfWidth * 1.28, this.halfHeight * 0.16)
			.fill({color: this.palette.base, alpha: 0.25});
		this.flare.ellipse(0, 0, this.halfWidth * 0.76, this.halfHeight * 0.075)
			.fill({color: this.palette.hot, alpha: 0.58});
		this.ring.clear();
		this.ring.ellipse(0, 0, this.halfWidth, this.halfHeight).stroke({
			width: 5,
			color: this.palette.hot,
			alpha: 0.78,
		});
		this.shockRing.clear();
		this.shockRing.ellipse(0, 0, this.halfWidth, this.halfHeight).stroke({
			width: 3,
			color: this.palette.light,
			alpha: 0.44,
		});
	}

	setColor(color) {
		this.draw(color);
	}

	spawnParticle(transform) {
		if(this.particles.length >= 64) return;
		const scaleY = Math.max(0.035, transform.scaleY);
		const scaleX = Math.max(0.18, transform.scaleX);
		const yLimit = this.halfHeight * scaleY * 0.90;
		const y = (Math.random() * 2 - 1) * yLimit;
		const normalizedY = Math.min(0.98, Math.abs(y) / Math.max(1, yLimit));
		const edgeX = this.halfWidth * scaleX * Math.sqrt(1 - normalizedY * normalizedY);
		const side = Math.random() < 0.5 ? -1 : 1;
		const particle = new Graphics();
		const size = 1.6 + Math.random() * 3.6;
		particle.ellipse(0, 0, size * 2.4, size * 0.72)
			.fill({color: Math.random() < 0.34 ? this.palette.hot : this.palette.light, alpha: 0.92});
		particle.circle(0, 0, size * 0.7).fill({color: this.palette.hot, alpha: 0.72});
		particle.blendMode = 'add';
		particle.position.set(side * edgeX, y);
		particle.__portalOpeningParticle = {
			age: 0,
			life: 0.28 + Math.random() * 0.38,
			vx: side * (42 + Math.random() * 105),
			vy: Math.sign(y || (Math.random() - 0.5)) * (8 + Math.random() * 46),
			spin: (Math.random() - 0.5) * 3.5,
		};
		particle.rotation = Math.atan2(
			particle.__portalOpeningParticle.vy,
			particle.__portalOpeningParticle.vx,
		);
		this.container.addChild(particle);
		this.particles.push(particle);
	}

	updateParticles(deltaSeconds) {
		const drag = Math.pow(0.96, deltaSeconds * 60);
		for(let index = this.particles.length - 1; index >= 0; index--) {
			const particle = this.particles[index];
			const state = particle.__portalOpeningParticle;
			state.age += deltaSeconds;
			if(state.age >= state.life) {
				this.particles.splice(index, 1);
				particle.destroy();
				continue;
			}
			const remaining = 1 - state.age / state.life;
			state.vx *= drag;
			state.vy *= drag;
			particle.x += state.vx * deltaSeconds;
			particle.y += state.vy * deltaSeconds;
			particle.rotation += state.spin * deltaSeconds;
			particle.alpha = Math.min(1, remaining * 2.6) * remaining;
			particle.scale.set(0.45 + remaining * 0.75);
		}
	}

	update(deltaSeconds, progress, transform) {
		const delta = Math.max(0, Math.min(0.06, Number(deltaSeconds) || 0));
		this.age += delta;
		const remaining = 1 - clamp01(this.age / PORTAL_OPEN_EFFECT_DURATION);
		const ignition = Math.sin(clamp01(progress) * Math.PI);
		this.flare.alpha = ignition * (0.72 + remaining * 0.28);
		this.flare.scale.set(
			0.55 + transform.scaleX * 0.72,
			0.52 + transform.scaleY * 0.50,
		);
		this.ring.alpha = remaining * (0.35 + ignition * 0.65);
		this.ring.scale.set(transform.scaleX, transform.scaleY);
		this.shockRing.alpha = remaining * remaining * 0.72;
		this.shockRing.scale.set(
			transform.scaleX * (1 + this.age * 1.4),
			transform.scaleY * (1 + this.age * 0.24),
		);

		if(progress < 0.88) {
			this.emissionAccumulator += delta * (58 + (1 - progress) * 90);
			const spawnCount = Math.min(8, Math.floor(this.emissionAccumulator));
			this.emissionAccumulator -= spawnCount;
			for(let index = 0; index < spawnCount; index++) this.spawnParticle(transform);
		}
		this.updateParticles(delta);
		return this.age < PORTAL_OPEN_EFFECT_DURATION || this.particles.length > 0;
	}

	destroy() {
		if(this.container && !this.container.destroyed) {
			this.container.destroy({children: true});
		}
		this.particles.length = 0;
		this.container = null;
	}
}
